<template>
  <div v-if="visible" class="dx-range-shell" :class="{ invalid: isInvalid }">
    <DxDateBox v-model="startInternal" :min="null" :max="endInternal || null" :display-format="DISPLAY_FORMAT"
      :show-clear-button="false" :input-attr="{ 'aria-label': 'Start date' }" class="range-part start"
      label="Start date" :label-mode="startInternal ? 'floating' : 'static'" type="date" open-on-field-click="true"
      show-drop-down-button="false" @value-changed="onStartChanged" />

    <span class="arrow">→</span>

    <DxDateBox v-model="endInternal" :min="startInternal || null" :display-format="DISPLAY_FORMAT"
      :show-clear-button="false" :input-attr="{ 'aria-label': 'End date' }" class="range-part end" label="End date"
      :label-mode="endInternal ? 'floating' : 'static'" type="date" open-on-field-click="true"
      show-drop-down-button="false" @value-changed="onEndChanged" />
  </div>
</template>

<script>
import { DxDateBox } from "devextreme-vue/date-box";

export default {
  name: "DateRangePicker",
  components: { DxDateBox },
  props: {
    /**
     * v-model payload:
     * { start: 'yyyy-MM-dd', end: 'yyyy-MM-dd' }
     */
    modelValue: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
    visible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue", "error:invalid-range", "warning:long-range"],

  data() {
    const today = new Date();
    const oneMonthAgo = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      today.getDate()
    );

    return {
      DISPLAY_FORMAT: "MMMM d, yyyy",
      startInternal: this.parseOrDefault(this.modelValue.start, oneMonthAgo),
      endInternal: this.parseOrDefault(this.modelValue.end, today),
      isInvalid: false,
    };
  },
  created() {
    this.emitUpdate();
  },

  watch: {
    modelValue: {
      immediate: false,
      deep: true,
      handler(newVal) {
        if (!newVal) return;
        this.startInternal = this.parseOrDefault(
          newVal.start,
          this.startInternal
        );
        this.endInternal = this.parseOrDefault(newVal.end, this.endInternal);
        this.validate();
      },
    },
  },

  methods: {
    parseOrDefault(str, def) {
      return str ? new Date(str + "T00:00:00") : def;
    },
    toIso(date) {
      if (!date) return null;
      return date.toISOString().substring(0, 10); // yyyy-MM-dd
    },
    dayDiff(a, b) {
      return Math.floor((b - a) / (1000 * 60 * 60 * 24));
    },

    onStartChanged() {
      this.emitUpdate();
      this.validate();
    },
    onEndChanged() {
      this.emitUpdate();
      this.validate();
    },
    emitUpdate() {
      this.$emit("update:modelValue", {
        start: this.toIso(this.startInternal),
        end: this.toIso(this.endInternal),
      });
    },

    validate() {
      this.isInvalid = false;
      if (this.startInternal && this.endInternal) {
        if (this.endInternal <= this.startInternal) {
          this.isInvalid = true;
          this.$emit("error:invalid-range");
          return;
        }

        const span = this.dayDiff(this.startInternal, this.endInternal);
        if (span > 90) {
          this.$emit("warning:long-range", span);
        }
      }
    },
  },
};
</script>

<style scoped>
.dx-range-shell {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  column-gap: 4px;
  flex-direction: row !important;
  margin: 0 5% 2%;
}

.dx-range-shell .dx-datebox {
  width: auto !important;
  min-width: 160px;
  flex: 1 1 0;
  width: 45% !important;
}

.dx-range-shell.invalid {
  border-color: var(--dx-color-error, #ff5252);
}

.range-part {
  flex: 1 1 0;
  min-width: 160px;
}

.range-part :deep(.dx-texteditor-input) {
  border: none !important;
  background: transparent !important;
}

.arrow {
  user-select: none;
  opacity: 0.6;
  font-size: 1.2em;
}
</style>
